/**
 * QC review mutation hooks.
 *
 * Four user-triggered mutations: run a QC sweep, acknowledge a fail,
 * force a re-ingestion, export a report.
 *
 * Each mutation invalidates the relevant cache keys on success so the
 * paired query hooks (`useQcLatestRun`, `useQcRunHistory`, plus any
 * ingestion or dashboard surface that consumes the same data) refetch
 * automatically.
 *
 * Split into one hook per mutation to keep each function under the
 * eslint max-lines-per-function budget (75) and so callers can opt into
 * only the mutations they need.
 */

import { useCallback, useState } from 'react'

import {
  acknowledgeQcResult,
  exportQcRun,
  forceQcRerun,
  runQcChecks,
} from '../services/qc'
import type {
  ExportFormat,
  QcCheckResult,
  QcRunWithResults,
} from '../types/qc'
import { useInvalidate } from './use-list-invalidation'
import { QC_RUNS_INVALIDATION_KEY } from './use-qc-runs'

/** Cache key for the dashboard QC alerts query (consumed by use-dashboard-data). */
export const QC_DASHBOARD_INVALIDATION_KEY = 'qc_dashboard_alerts'

// ---------------------------------------------------------------------------
// runQc — trigger a synchronous QC sweep
// ---------------------------------------------------------------------------

interface UseRunQcResult {
  run: (caseId: string, runId: string) => Promise<QcRunWithResults | null>
  running: boolean
  error: string | null
  clearError: () => void
}

export function useRunQc(): UseRunQcResult {
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const invalidate = useInvalidate()

  const run = useCallback(
    async (caseId: string, runId: string) => {
      setRunning(true)
      setError(null)
      try {
        const result = await runQcChecks(caseId, runId)
        invalidate([QC_RUNS_INVALIDATION_KEY, QC_DASHBOARD_INVALIDATION_KEY, 'case_activity'])
        return result
      } catch (error_) {
        setError(error_ instanceof Error ? error_.message : 'Failed to run QC checks')
        return null
      } finally {
        setRunning(false)
      }
    },
    [invalidate]
  )

  const clearError = useCallback(() => { setError(null); }, [])
  return { run, running, error, clearError }
}

// ---------------------------------------------------------------------------
// acknowledge — acknowledge a single fail with written justification
// ---------------------------------------------------------------------------

export interface AcknowledgeQcInput {
  caseId: string
  runId: string
  qcRunId: string
  resultId: string
  reason: string
}

interface UseAcknowledgeQcResult {
  acknowledge: (input: AcknowledgeQcInput) => Promise<QcCheckResult | null>
  acknowledging: boolean
  error: string | null
  clearError: () => void
}

export function useAcknowledgeQc(): UseAcknowledgeQcResult {
  const [acknowledging, setAcknowledging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const invalidate = useInvalidate()

  const acknowledge = useCallback(
    async (input: AcknowledgeQcInput) => {
      setAcknowledging(true)
      setError(null)
      try {
        const result = await acknowledgeQcResult(input)
        invalidate([QC_RUNS_INVALIDATION_KEY, QC_DASHBOARD_INVALIDATION_KEY])
        return result
      } catch (error_) {
        setError(error_ instanceof Error ? error_.message : 'Failed to acknowledge QC fail')
        return null
      } finally {
        setAcknowledging(false)
      }
    },
    [invalidate]
  )

  const clearError = useCallback(() => { setError(null); }, [])
  return { acknowledge, acknowledging, error, clearError }
}

// ---------------------------------------------------------------------------
// forceRerun — reset the ingestion run for re-ingest after a QC fail
// ---------------------------------------------------------------------------

export interface ForceQcRerunInput {
  caseId: string
  runId: string
  reason: string
}

interface UseForceQcRerunResult {
  forceRerun: (input: ForceQcRerunInput) => Promise<boolean>
  rerunning: boolean
  error: string | null
  clearError: () => void
}

export function useForceQcRerun(): UseForceQcRerunResult {
  const [rerunning, setRerunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const invalidate = useInvalidate()

  const forceRerun = useCallback(
    async (input: ForceQcRerunInput) => {
      setRerunning(true)
      setError(null)
      try {
        await forceQcRerun(input)
        // forceRerun resets receipt -> pending and marks the run corrupt,
        // so we invalidate ingestion + QC + dashboard caches.
        invalidate([
          'ingestion_runs',
          QC_RUNS_INVALIDATION_KEY,
          QC_DASHBOARD_INVALIDATION_KEY,
          'case_activity',
        ])
        return true
      } catch (error_) {
        setError(error_ instanceof Error ? error_.message : 'Failed to force QC re-run')
        return false
      } finally {
        setRerunning(false)
      }
    },
    [invalidate]
  )

  const clearError = useCallback(() => { setError(null); }, [])
  return { forceRerun, rerunning, error, clearError }
}

// ---------------------------------------------------------------------------
// exportRun — download a CSV or JSON QC report
// ---------------------------------------------------------------------------

export interface ExportQcRunInput {
  caseId: string
  runId: string
  qcRunId: string
  format: ExportFormat
}

interface UseExportQcResult {
  exportRun: (input: ExportQcRunInput) => Promise<boolean>
  exporting: boolean
  error: string | null
  clearError: () => void
}

/**
 * Triggers a browser download via a temporary anchor element. The download
 * uses the filename returned by the server in `Content-Disposition`.
 */
export function useExportQc(): UseExportQcResult {
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const exportRun = useCallback(async (input: ExportQcRunInput) => {
    setExporting(true)
    setError(null)
    try {
      const { blob, filename } = await exportQcRun(input)
      triggerDownload(blob, filename)
      return true
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Failed to export QC report')
      return false
    } finally {
      setExporting(false)
    }
  }, [])

  const clearError = useCallback(() => { setError(null); }, [])
  return { exportRun, exporting, error, clearError }
}

/**
 * Save a Blob to the user's downloads via a synthetic anchor click.
 * Pattern is browser-standard — no DOM library required.
 */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
